import { useState } from "react"
import { enqueueSnackbar } from "notistack"
import { MenuItem, Stack, TextField, Typography } from "@mui/material"
import { makeStyles } from "tss-react/mui"

import { useAppLocalization } from "../../../../../../localization/components/AppLocalizationProvider"
import { LocalizedStrict } from "../../../../../../localization/components/AppLocalized"
import { StandardButton } from "../../../../../../../shared/components/buttons/StandardButton"
import { StandardDialog } from "../../../../../../../shared/components/dialogs/StandardDialog"

import { useWeekMenu } from "../../WeekMenuContext"
import { WeekMenuOverwriteCheckbox } from "./WeekMenuOverwriteCheckbox"

const useStyles = makeStyles()((theme) => ({
    content: {
        padding: "0 1.5rem 1.5rem",
    },
    sourceCard: {
        padding: ".625rem .75rem",
        border: `1px solid ${theme.palette.tailwind.gray[200]}`,
        borderRadius: ".5rem",
        background: theme.palette.tailwind.gray[50],
    },
    sourceMeta: {
        color: theme.palette.tailwind.gray[500],
    },
}))

export type CopyWeekTarget = Readonly<{
    storeKey: string
    label: string
}>

type Props = Readonly<{
    open: boolean
    sourceTitle: string
    sourcePeriod: string
    targets: ReadonlyArray<CopyWeekTarget>
    onClose: () => void
}>

export function CopyWeekDialog({ open, sourceTitle, sourcePeriod, targets, onClose }: Props) {
    const { classes } = useStyles()
    const { l10n } = useAppLocalization()
    const { actions } = useWeekMenu()

    const [selectedKey, setSelectedKey] = useState(targets[0]?.storeKey ?? "")
    const [overwriteFilled, setOverwriteFilled] = useState(false)

    const optionLabel = (target: CopyWeekTarget, index: number) =>
        index === 0 ? l10n.getString("merchant-week-menu-copy-week-next-prefix", { label: target.label }) : target.label

    const handleCopy = () => {
        const target = targets.find((candidate) => candidate.storeKey === selectedKey)
        if (!target) return
        actions.copyWeekTo([target.storeKey], overwriteFilled)
        enqueueSnackbar({
            variant: "success",
            message: l10n.getString("merchant-week-menu-copy-week-snackbar", { week: target.label }),
        })
        onClose()
    }

    return (
        <LocalizedStrict id="merchant-week-menu-copy-week-dialog" attrs={{ dialogTitle: true }}>
            <StandardDialog open={open} dialogTitle="Copy week to other weeks" onClose={onClose}>
                <Stack spacing="1rem" className={classes.content}>
                    <Stack spacing=".125rem" className={classes.sourceCard}>
                        <Typography variant="subtitle2">{sourceTitle}</Typography>
                        <LocalizedStrict
                            id="merchant-week-menu-copy-week-source-subtitle"
                            vars={{ period: sourcePeriod }}
                        >
                            <Typography variant="caption" className={classes.sourceMeta}>
                                Full week menu · {sourcePeriod}
                            </Typography>
                        </LocalizedStrict>
                    </Stack>

                    {/* Attr-only key: a message VALUE here would make Fluent replace the TextField's
                        children (the MenuItems) with plain text and crash the MUI Select. */}
                    <LocalizedStrict id="merchant-week-menu-copy-week-to-label" attrs={{ label: true }}>
                        <TextField
                            select
                            label="Copy to"
                            value={selectedKey}
                            fullWidth
                            onChange={(event) => setSelectedKey(event.target.value)}
                        >
                            {targets.map((target, index) => (
                                <MenuItem key={target.storeKey} value={target.storeKey}>
                                    {optionLabel(target, index)}
                                </MenuItem>
                            ))}
                        </TextField>
                    </LocalizedStrict>

                    <WeekMenuOverwriteCheckbox checked={overwriteFilled} onChange={setOverwriteFilled} />

                    <Stack direction="row" justifyContent="flex-end" spacing=".75rem">
                        <LocalizedStrict id="merchant-week-menu-copy-week-cancel-button">
                            <StandardButton fullWidth={false} variant="outlined" color="black" onClick={onClose}>
                                Cancel
                            </StandardButton>
                        </LocalizedStrict>
                        <LocalizedStrict id="merchant-week-menu-copy-week-confirm-button">
                            <StandardButton fullWidth={false} disabled={selectedKey === ""} onClick={handleCopy}>
                                Copy
                            </StandardButton>
                        </LocalizedStrict>
                    </Stack>
                </Stack>
            </StandardDialog>
        </LocalizedStrict>
    )
}
